import ssmlCheck from 'ssml-check'

import { PropertySsmlValidationResult, SsmlValidationIssue } from '../types'
import { shouldIssueBeIgnored } from './issueCategorization/shouldIssueBeIgnored'
import { not } from '../../../lib/functionChaining'

export const CONTAINS_TAGS = /(<([^>]+)>)/gi

export const createSSMLError = (errorText: string) => ({ type: errorText })

const getDefaulError = (
  validatedText: string,
  identifiers: { entityId: string; propertyName: string }
) => ({
  ...identifiers,
  __isPropertyValidationResult: true as const,
  validatedText,
  errors: [createSSMLError("Can't parse SSML")]
})

export async function runSsmlValidation(
  validatedText: string | undefined,
  identifiers: { entityId: string; propertyName: string } // a bit of an antipattern to pass-through a prop, but seems more sensible than splitting SsmlValidationResult interface just for this reason
): Promise<PropertySsmlValidationResult | undefined> {
  if (validatedText === undefined || validatedText === null) {
    return undefined
  }

  if (!containsSsmlElement(validatedText)) {
    return
  }

  // Validation library is very inflexible and its specs differ from our use-case,
  // so we need to adjust our texts to pass flawlessly
  const adjustedText = removeContourAttribute(ensureSpeakElementPresence(validatedText))

  return ssmlCheck
    .verifyAndFix(adjustedText, { validateAudioFiles: true })
    .then(result => ({
      ...result,
      validatedText,
      ...identifiers,
      __isPropertyValidationResult: true as const
    }))
    .then(appendErrors(runCustomValidation(adjustedText)))
    .then(omitIgnoredErrors)
    .catch(() => getDefaulError(validatedText, identifiers))
}

export function isTextWrappedInSpeakElement(text: string): boolean {
  return new RegExp(/(<speak>).*(<\/speak>)/).test(text)
}

export function ensureSpeakElementPresence(text: string): string {
  if (isTextWrappedInSpeakElement(text)) {
    return text
  }

  return `<speak>${text}</speak>`
}

export function removeContourAttribute(text: string): string {
  return text.replace(new RegExp('contour="(.*?)"', 'gui'), '')
}

export function containsSsmlElement(text: string): boolean {
  return new RegExp(/(<([^>]+)>)/).test(text)
}

const appendErrors =
  (errorsToAppend: SsmlValidationIssue[]) => (validationResult: PropertySsmlValidationResult) => {
    const nativeErrors = validationResult.errors ?? []
    return { ...validationResult, errors: [...nativeErrors, ...errorsToAppend] }
  }

//note: probably runCustomValidation will be used in near future
const runCustomValidation = (text: string): SsmlValidationIssue[] => {
  const issues: SsmlValidationIssue[] = []

  const a0 = String.fromCharCode(160)
  if (text.includes(a0)) {
    const index = text.indexOf(a0)
    issues.push({ type: `Forbidden white space at index "${index}"` })
  }

  return issues
}

const omitIgnoredErrors = (validationResult: PropertySsmlValidationResult) => {
  return {
    ...validationResult,
    errors: validationResult.errors?.filter(not(shouldIssueBeIgnored))
  }
}
